import { countConditions } from "@estelle/domain";

import { apiFetch } from "@/adapters/api/base";
import type {
  Contact,
  ContactCustomField,
  ContactsRepository,
  ContactType,
  ContactWithDetails,
  DirectoryEntry,
  Id,
  ListQuery,
  Page,
  WriteResult,
} from "@estelle/domain";

/**
 * Contacts, over HTTP (M12-4, FR-CON-1 … 13).
 *
 * Owned entirely by the ERP — no Shopify reads or writes (FR-CON-13) — so
 * unlike inventory and warehouses, nothing here is stubbed: every method is a
 * real Postgres read or write, and there is no fixture fallback to compose
 * over.
 */

const BASE = "/api/contacts";

function listParams(query: ListQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.cursor !== undefined) params.set("cursor", query.cursor);
  if (query.search !== undefined && query.search !== "") {
    params.set("search", query.search);
  }
  if (query.filter && countConditions(query.filter) > 0) {
    params.set("filter", JSON.stringify(query.filter));
  }
  if (query.sort && query.sort.length > 0) {
    params.set(
      "sort",
      query.sort.map((entry) => `${entry.column}:${entry.direction}`).join(","),
    );
  }
  return params;
}

async function get<T>(path: string, params?: URLSearchParams): Promise<T> {
  const query = params?.toString() ?? "";
  const response = await apiFetch(`${BASE}${path}${query ? `?${query}` : ""}`);
  if (!response.ok) {
    throw new Error(
      `Contacts request failed: ${String(response.status)} on ${path || "/"}`,
    );
  }
  return (await response.json()) as T;
}

/**
 * A write's outcome. A refusal arrives as a `WriteResult` body with a 4xx
 * status — a rejection to show, not a failure to throw. Anything else is a
 * transport failure, which does throw.
 */
async function write<T>(
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  body?: unknown,
): Promise<WriteResult<T>> {
  const response = await apiFetch(`${BASE}${path}`, {
    method,
    headers:
      body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<T> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  throw new Error(
    `Contacts ${method} failed: ${String(response.status)} on ${path || "/"}`,
  );
}

export function createApiContacts(): ContactsRepository {
  return {
    list: (query) => get<Page<Contact>>("", listParams(query)),

    // Contacts and Shopify's customers in one list (#493). What comes back —
    // customers at all, and their phone and email — is the server's decision,
    // made from the caller's permissions; the body is passed through as it is.
    directory: (query) =>
      get<Page<DirectoryEntry>>("/directory", listParams(query)),

    get: async (id: Id): Promise<ContactWithDetails | null> => {
      const response = await apiFetch(`${BASE}/${encodeURIComponent(id)}`);
      if (response.status === 404) return null;
      if (!response.ok) {
        throw new Error(`Contacts request failed: ${String(response.status)}`);
      }
      return (await response.json()) as ContactWithDetails;
    },

    create: (input) => write<Contact>("", "POST", input),

    update: (id, patch) =>
      write<Contact>(`/${encodeURIComponent(id)}`, "PATCH", patch),

    archive: (id) =>
      write<Contact>(`/${encodeURIComponent(id)}/archive`, "POST"),

    delete: async (id) => {
      const result = await write<null>(`/${encodeURIComponent(id)}`, "DELETE");
      return result.ok ? { ok: true, data: undefined } : result;
    },

    setCustomFields: (id, fields) =>
      write<ContactCustomField[]>(
        `/${encodeURIComponent(id)}/custom-fields`,
        "PATCH",
        { fields },
      ),

    listTypes: async () => {
      const response = await apiFetch(`${BASE}/types`);
      if (!response.ok) {
        throw new Error(`Contacts request failed: ${String(response.status)}`);
      }
      return (await response.json()) as ContactType[];
    },

    createType: (name) => write<ContactType>("/types", "POST", { name }),

    renameType: (id, name) =>
      write<ContactType>(`/types/${encodeURIComponent(id)}`, "PATCH", {
        name,
      }),

    deleteType: async (id) => {
      const result = await write<null>(
        `/types/${encodeURIComponent(id)}`,
        "DELETE",
      );
      return result.ok ? { ok: true, data: undefined } : result;
    },
  };
}
